/**
 * Build ThemeDefinition from DeskTokenSet for Ant ThemeSystem + CSS vars.
 */

import type { ThemeDefinition } from '@/shared/theme/types/theme.types'
import type { ColorTheme } from '@/shared/types/theme.types'
import {
  type DeskTokenSet,
  hexToRgbTriplet,
} from '@/shared/theme/systems/desk-tokens'

export function buildThemeDefinition(
  id: ColorTheme | string,
  name: string,
  variant: 'light' | 'dark',
  t: DeskTokenSet
): ThemeDefinition {
  const primaryRgb = hexToRgbTriplet(t.primary)
  const isDark = variant === 'dark'
  // Hover / active metal steps stay in the cool ice stream
  const primaryHover = isDark ? '#D7EAF4' : '#1B3D50'
  const primaryActive = isDark ? '#8FB4C8' : '#163242'
  const primaryLight = isDark ? '#D7EAF4' : '#3A6A82'
  const primaryDark = isDark ? '#8FB4C8' : '#163242'
  // Text ladder — light: all steps AA on sand canvas (pro-max). Never #8A7366 on sand.
  const textTertiary = isDark ? '#929292' : '#4A3A32'
  const textDisabled = isDark ? '#7A6558' : '#5C4A40'
  const textQuaternary = isDark ? '#7A6558' : '#5C4A40'
  // Border secondary = softer grid for nested chrome
  const borderLight = isDark ? '#564338' : '#C4A888'

  return {
    id,
    name,
    variant,
    colors: {
      primary: {
        primary: t.primary,
        primaryLight,
        primaryDark,
        primaryHover,
        primaryActive,
        primaryDeep: primaryDark,
        primaryDeepest: primaryDark,
        primaryRgb,
      },
      semantic: {
        success: t.bull,
        successLight: isDark ? '#4A9270' : '#16A34A',
        successDark: isDark ? '#2F6149' : '#166534',
        successRgb: hexToRgbTriplet(t.bull),
        // Amber caution ladder — never brand metal as risk alone
        warning: isDark ? '#E3B341' : '#8A6A08',
        warningLight: isDark ? '#F0C14D' : '#A67C0A',
        warningDark: isDark ? '#C49220' : '#6B5408',
        warningRgb: isDark ? '227, 179, 65' : '138, 106, 8',
        error: t.bear,
        errorLight: isDark ? '#D47878' : '#DC2626',
        errorDark: isDark ? '#A34848' : '#991B1B',
        errorRgb: hexToRgbTriplet(t.bear),
        // Info stays warm-taupe in brand stream (not tech blue chrome)
        info: isDark ? '#B8B8B8' : '#5C4036',
        infoLight: isDark ? '#E8D8C8' : '#7A5A4C',
        infoDark: isDark ? '#A89080' : '#3A2A22',
      },
      surface: {
        background: t.canvas,
        surface: t.canvas,
        surfacePrimary: t.canvas,
        surfaceSecondary: t.elevated,
        surfaceTertiary: t.container,
        surfaceQuaternary: t.grid,
        card: t.container,
        cardHover: t.elevated,
        cardActive: t.canvas,
        cardElevated: t.elevated,
        cardFloating: t.elevated,
        cardLayer1: t.canvas,
        cardLayer2: t.canvas,
        cardLayer3: t.container,
        cardLayer4: t.elevated,
        cardLayer5: t.grid,
      },
      text: {
        text: t.textPrimary,
        textPrimary: t.textPrimary,
        textSecondary: t.textSecondary,
        textTertiary,
        textQuaternary,
        textDisabled,
        // On ice metal: dark cool ink for AA.
        // Cream-on-metal fails contrast and reads as “washed flashlight.”
        textOnStrong: isDark ? t.canvas : t.textPrimary,
        textOnLight: t.textPrimary,
      },
      border: {
        border: t.grid,
        borderLight,
        borderDark: t.grid,
        borderFocus: t.primary,
        accent: t.bull,
        accentLight: t.bull,
        accentDark: t.bull,
        accentRgb: hexToRgbTriplet(t.bull),
      },
    },
    shadows: isDark
      ? {
          sm: '0 4px 12px rgba(0,0,0,0.35)',
          md: '0 8px 22px rgba(0,0,0,0.4)',
          lg: '0 12px 32px rgba(0,0,0,0.45)',
          xl: '0 16px 40px rgba(0,0,0,0.5)',
          '2xl': '0 24px 56px rgba(0,0,0,0.55)',
        }
      : {
          sm: '0 1px 2px rgba(28,32,46,0.06)',
          md: '0 4px 12px rgba(28,32,46,0.08)',
          lg: '0 12px 28px rgba(28,32,46,0.1)',
          xl: '0 20px 40px rgba(28,32,46,0.12)',
          '2xl': '0 28px 56px rgba(28,32,46,0.14)',
        },
    neutral: {
      '50': t.textPrimary,
      '100': t.textSecondary,
      '200': t.grid,
      '300': t.grid,
      '400': t.textSecondary,
      '500': t.grid,
      '600': t.elevated,
      '700': t.container,
      '800': t.canvas,
      '900': t.canvas,
    },
    focus: {
      color: t.primary,
      ring: `rgba(${primaryRgb}, 0.32)`,
    },
  }
}
